import React, {memo} from "react"
import {useTranslation} from "react-i18next";


import FormSection from "../../../components/FormSection";
import Model23Select from "./Model23Select";
import Model23MeasureField from "./Model23MeasureField";
import MeasurementsList from "./MeasurementsList";
import {Checkbox} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import {formikPropertyChanged, generateMeasurementKey} from "./index";
import MDTypography from "../../../materialDashboard/components/MDTypography";


const StratigraphicProfile = memo(({formik, model23Registry, emptyMeasure, inputDisabled}) => {
    const {t} = useTranslation(['models23']);
    const stratigraphicProfileRegistry = model23Registry.data?.stratigraphicProfileRegistry;

    const GrainShapeVersionRegistry = stratigraphicProfileRegistry?.grainShapeVersion;
    const grainShapeVersion = GrainShapeVersionRegistry?.find(r => r.code === formik.values.grainShapeVersion);

    const RRegistry = stratigraphicProfileRegistry?.hardness?.sort((a, b) => a.index - b.index);
    const WetnessRegistry = stratigraphicProfileRegistry?.wetness;
    const GrainShapeRegistry = stratigraphicProfileRegistry?.grainShapes?.find(r => r.version === grainShapeVersion?.code)?.shapes;
    const GrainShape3Registry = stratigraphicProfileRegistry?.grainShapeTertiary;

    const profile = formik.values.stratigraphicProfile;

    const headers = [
        t('model23Form.stratigraphicProfileField.top'),
        t('model23Form.stratigraphicProfileField.bottom'),
        t('model23Form.stratigraphicProfileField.wetness'),
        t('model23Form.stratigraphicProfileField.grainShapePrimary'),
        t('model23Form.stratigraphicProfileField.grainShapeSecondary'),
        t('model23Form.stratigraphicProfileField.grainShapeTertiary'),
        t('model23Form.stratigraphicProfileField.fg2'),
        t('model23Form.stratigraphicProfileField.grainSizeAvg'),
        t('model23Form.stratigraphicProfileField.grainSizeMax'),
        t('model23Form.stratigraphicProfileField.hardness'),
        t('model23Form.stratigraphicProfileField.notes')
    ]

    const createEmptyMeasure = (measureBefore, measureAfter) => {
        return {
            ...emptyMeasure,
            top: measureBefore?.bottom || null,
            bottom: measureAfter?.top || null,
            _key: generateMeasurementKey(),
        }
    }

    const onMeasurementsUpdate = (measurements) => {
        formik.setFieldValue(`stratigraphicProfile`, measurements);
    }

    const renderMeasure = (row, index) => {

        const disableTertiaryShape = formik.values.stratigraphicProfile[index]?.grainShapePrimary !== '6m';

        const handleChangePrimaryShape = async (e) => {
            await formik.handleChange(e);
            if(e.target.value !== '6m') {
                formik.setFieldValue(`stratigraphicProfile[${index}].grainShapeTertiary`, null);
            }
        }

        return <>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`stratigraphicProfile[${index}].top`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`stratigraphicProfile[${index}].bottom`}
                                 unitOfMeasure="cm"
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23Select formik={formik}
                           label=""
                           labelFn={r => `${r.index} - ${r.term}`}
                           name={`stratigraphicProfile[${index}].wetness`}
                           registry={WetnessRegistry}
                           disabled={inputDisabled}/>
            <Model23Select formik={formik}
                           label=""
                           attrValue="subClassId"
                           labelFn={r => `${r.subClassId} - ${r.comboDesc}`}
                           name={`stratigraphicProfile[${index}].grainShapePrimary`}
                           registry={GrainShapeRegistry}
                           onChange={handleChangePrimaryShape}
                           disabled={inputDisabled}/>
            <Model23Select formik={formik}
                           label=""
                           attrValue="subClassId"
                           labelFn={r => `${r.subClassId} - ${r.comboDesc}`}
                           name={`stratigraphicProfile[${index}].grainShapeSecondary`}
                           registry={GrainShapeRegistry}
                           disabled={inputDisabled}/>
            <Model23Select formik={formik}
                           label=""
                           attrValue="subClassId"
                           labelFn={r => `${r.subClassId} - ${r.comboDesc}`}
                           name={`stratigraphicProfile[${index}].grainShapeTertiary`}
                           registry={GrainShape3Registry}
                           disabled={inputDisabled || disableTertiaryShape}/>
            <FormControlLabel control={<Checkbox checked={formik.values.stratigraphicProfile[index]?.fg2}
                                                 onChange={(e) => formik.setFieldValue(`stratigraphicProfile[${index}].fg2`, e.target.checked)}
                                                 name={`stratigraphicProfile[${index}].fg2`}
                                                 color="info"
                                                 inputProps={{ 'aria-label': 'controlled' }}
                                                 size="small"/>}
                              label=""
                              disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`stratigraphicProfile[${index}].grainSizeAvg`}
                                 unitOfMeasure=""
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`stratigraphicProfile[${index}].grainSizeMax`}
                                 unitOfMeasure=""
                                 type="number"
                                 disabled={inputDisabled}/>
            <Model23Select formik={formik}
                           label=""
                           labelFn={r => `${r.index} - ${r.object} (${r.range})`}
                           name={`stratigraphicProfile[${index}].hardness`}
                           registry={RRegistry}
                           disabled={inputDisabled}/>
            <Model23MeasureField formik={formik}
                                 label=""
                                 name={`stratigraphicProfile[${index}].notes`}
                                 unitOfMeasure=""
                                 disabled={inputDisabled}/>
        </>
    }

    return <FormSection title={t('model23Form.stratigraphicProfile')}
                        openDefault={false}
                        error={Boolean(formik.errors.stratigraphicProfile) && formik.touched.stratigraphicProfile}>
        <MDTypography variant="caption" color="text" fontWeight="bold">
            { grainShapeVersion?.description ?? "" }
        </MDTypography>
        <MeasurementsList headers={headers}
                          measurements={profile}
                          createEmptyMeasure={createEmptyMeasure}
                          onMeasurementsUpdate={onMeasurementsUpdate}
                          renderMeasure={renderMeasure}
                          inputDisabled={inputDisabled}/>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, 'stratigraphicProfile', true) && prevProps.model23Registry === nextProps.model23Registry);

export default StratigraphicProfile;